// The site's part of the tamper-evident log, newest first.

import { useState } from 'react';
import { api, message } from '../../api.ts';
import { logLine, when } from '../../format.ts';
import type { LogView } from '../../types.ts';
import { Chip, Loading, Problem, useLoad } from '../../ui.tsx';

export function Log({ siteId }: { siteId: string }) {
  const first = useLoad(() => api<LogView>('GET', `/sites/${siteId}/log`), [siteId]);
  const [older, setOlder] = useState<LogView['rows']>([]);
  const [error, setError] = useState<string | null>(null);
  const rows = [...(first.data?.rows ?? []), ...older];
  const more = async () => {
    try {
      const last = rows[rows.length - 1];
      const next = await api<LogView>('GET', `/sites/${siteId}/log?before=${last.seq}`);
      setOlder((o) => [...o, ...next.rows]);
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <div className="stack">
      {first.error && <Problem text={first.error} />}
      {first.loading && !first.data && <Loading />}
      {first.data && (
        <>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            {first.data.intact ? <Chip tone="green">Checked: nothing in this log has been changed or removed</Chip> : <Chip tone="red">Warning: the log's check failed. Contact the Z.A.S. team lead now.</Chip>}
            <a className="btn small" href={`/console-api/sites/${siteId}/log.csv`} download>
              Download (CSV)
            </a>
          </div>
          <div className="table">
            {rows.length === 0 && <div className="empty">Nothing yet.</div>}
            {rows.map((r) => (
              <div key={r.seq} className="tr" style={{ gridTemplateColumns: '150px minmax(0, 1fr) 70px' }}>
                <span className="mono muted">{when(r.at)}</span>
                <span>{logLine(r.action, r.details)}</span>
                <span className="faint small mono" style={{ textAlign: 'right' }}>
                  #{r.seq}
                </span>
              </div>
            ))}
          </div>
          {error && <p className="error">{error}</p>}
          {first.data.rows.length === 100 && (
            <button className="btn small" style={{ alignSelf: 'flex-start' }} onClick={more}>
              Show older entries
            </button>
          )}
        </>
      )}
    </div>
  );
}
